import { memo } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import ThumbnailBeat from '../thumbnailBeat';
import useBeatPreviewStrip from './useBeatPreviewStrip';

export type BeatPreviewStripProps = {
  id?: string;
  activeButton: any;
  ecgDataMap: any;
  beat: any;
  offset?: number;
  index: number;
  channels: any;
  gain?: number;
  width: number;
  height: number;
  highPass?: number;
  isSelected?: boolean;
  isActive?: boolean;
  onClickSelectBeat: (beat: any, index: number) => void;
};

/**
 * Renders one beat thumbnail in the R-R heatmap strip list, reflecting any pending type or reviewed change. Draws ThumbnailBeat from the ECG data, beat markers, HES status and channels returned by useBeatPreviewStrip, and appends a tooltip when the beat carries a change that has not been applied yet.
 *
 * @param props - Thumbnail inputs including the beat, its element id, gain, size, high-pass setting and selection flags.
 * @returns The beat thumbnail, optionally followed by an unapplied-change tooltip.
 */
const BeatPreviewStrip = ({
  id,
  activeButton,
  ecgDataMap,
  beat,
  offset,
  index,
  channels: channelsProp,
  gain,
  width,
  height,
  highPass,
  isSelected,
  isActive,
  onClickSelectBeat: onClickSelectBeatProp,
}: BeatPreviewStripProps) => {
  const {
    ecgData,
    beatMarkers,
    hesBeatStatus,
    channels,
    beatsTypeChange,
    beatReviewedChange,
    isChanged,
    displayTemplateId,
    onClickSelectBeat,
  } = useBeatPreviewStrip({
    activeButton,
    ecgDataMap,
    beat,
    offset,
    index,
    channels: channelsProp,
    onClickSelectBeat: onClickSelectBeatProp,
  });

  return (
    <>
      {/* ThumbnailBeat's onClickSelect (Task 7) is typed () => void, but this handler reads
          the click event to skip shift/ctrl/meta clicks - pre-existing. */}
      <ThumbnailBeat
        id={id}
        type={beatsTypeChange?.type || beat?.type}
        onClickSelect={onClickSelectBeat as any}
        ecgData={ecgData}
        beatMarkers={beatMarkers}
        hesBeatStatus={hesBeatStatus}
        channels={channels}
        gain={gain}
        width={width}
        height={height}
        highPass={highPass}
        isSelected={isSelected}
        isActive={isActive}
        isChanged={isChanged}
        isReviewed={beatReviewedChange.isReviewed}
        morphologyName={displayTemplateId}
      />
      {isChanged ? (
        <UncontrolledTooltip
          className="custom-tooltip"
          target={id}
          placement="bottom"
          delay={{ show: 0, hide: 0 }}
          hideArrow
        >
          The change has not been applied
        </UncontrolledTooltip>
      ) : null}
    </>
  );
};

export default memo(BeatPreviewStrip);
